import type { ReactNode } from "react";
import { ResponsiveImage } from "@/components/responsive-image";

type HubHeroProps = {
  /** Fallback JPEG source for the hero image. */
  src: string;
  avifSrcSet?: string;
  webpSrcSet?: string;
  fallbackSrcSet?: string;
  placeholder?: string;
  /** Meaningful alternative text describing what is actually visible. */
  alt: string;
  width?: number;
  height?: number;
  children: ReactNode;
};

/**
 * Full-bleed hub hero. The image is a real <img> (via ResponsiveImage) so it
 * carries accurate alt text; the darkening layers are decorative divs hidden
 * from assistive technology. Explicit width/height plus a fixed-position
 * absolute wrapper mean the hero introduces no layout shift.
 *
 * The scrim is directional: heavy on the left where the text lives and
 * almost clear on the right so the background photograph stays visible.
 */
export function HubHero({
  src,
  avifSrcSet,
  webpSrcSet,
  fallbackSrcSet,
  placeholder,
  alt,
  width = 1920,
  height = 1080,
  children,
}: HubHeroProps) {
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0 -z-10">
        <ResponsiveImage
          src={src}
          avifSrcSet={avifSrcSet}
          webpSrcSet={webpSrcSet}
          fallbackSrcSet={fallbackSrcSet}
          placeholder={placeholder}
          sizes="100vw"
          alt={alt}
          width={width}
          height={height}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-right animate-hero-reveal"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-black/10"
        ></div>
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80"
        ></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-14 pb-14 sm:pt-20 sm:pb-16">{children}</div>
    </section>
  );
}
